import log from "loglevel";

export type TrackingParams = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  fbclid?: string;
  gclid?: string;
  wbraid?: string;
  captured_at?: string;
};

const STORAGE_KEY = "tracking_params";
const MAX_UTM_SOURCE = 100;
const MAX_UTM_MEDIUM = 100;
const MAX_UTM_CAMPAIGN = 255;
const MAX_CLICK_ID = 500;

function clamp(s: string, max: number): string {
  return s.length > max ? s.slice(0, max) : s;
}

function safeTrimmedString(v: unknown): string | null {
  if (typeof v !== "string") return null;
  const s = v.trim();
  return s ? s : null;
}

export function parseTrackingParamsFromSearch(
  search: string
): TrackingParams | null {
  const params = new URLSearchParams(search);
  const utmSource = safeTrimmedString(params.get("utm_source"));
  const utmMedium = safeTrimmedString(params.get("utm_medium"));
  const utmCampaign = safeTrimmedString(params.get("utm_campaign"));
  const fbclid = safeTrimmedString(params.get("fbclid"));
  const gclid = safeTrimmedString(params.get("gclid"));
  const wbraid = safeTrimmedString(params.get("wbraid"));

  if (!utmSource && !utmMedium && !utmCampaign && !fbclid && !gclid && !wbraid)
    return null;

  return {
    ...(utmSource ? { utm_source: clamp(utmSource, MAX_UTM_SOURCE) } : {}),
    ...(utmMedium ? { utm_medium: clamp(utmMedium, MAX_UTM_MEDIUM) } : {}),
    ...(utmCampaign
      ? { utm_campaign: clamp(utmCampaign, MAX_UTM_CAMPAIGN) }
      : {}),
    ...(fbclid ? { fbclid: clamp(fbclid, MAX_CLICK_ID) } : {}),
    ...(gclid ? { gclid: clamp(gclid, MAX_CLICK_ID) } : {}),
    ...(wbraid ? { wbraid: clamp(wbraid, MAX_CLICK_ID) } : {}),
    captured_at: new Date().toISOString(),
  };
}

export function persistTrackingParams(): void {
  try {
    if (typeof window === "undefined") return;
    const tracking = parseTrackingParamsFromSearch(window.location.search);
    if (!tracking) return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(tracking));
  } catch (error) {
    log.error("Error persisting tracking params from current URL", error);
  }
}

export function getTrackingParams(): TrackingParams | null {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return null;
    const obj = parsed as Record<string, unknown>;

    const utmSource = safeTrimmedString(obj.utm_source);
    const utmMedium = safeTrimmedString(obj.utm_medium);
    const utmCampaign = safeTrimmedString(obj.utm_campaign);
    const fbclid = safeTrimmedString(obj.fbclid);
    const gclid = safeTrimmedString(obj.gclid);
    const wbraid = safeTrimmedString(obj.wbraid);
    const capturedAt = safeTrimmedString(obj.captured_at);

    if (
      !utmSource &&
      !utmMedium &&
      !utmCampaign &&
      !fbclid &&
      !gclid &&
      !wbraid
    )
      return null;

    return {
      ...(utmSource ? { utm_source: clamp(utmSource, MAX_UTM_SOURCE) } : {}),
      ...(utmMedium ? { utm_medium: clamp(utmMedium, MAX_UTM_MEDIUM) } : {}),
      ...(utmCampaign
        ? { utm_campaign: clamp(utmCampaign, MAX_UTM_CAMPAIGN) }
        : {}),
      ...(fbclid ? { fbclid: clamp(fbclid, MAX_CLICK_ID) } : {}),
      ...(gclid ? { gclid: clamp(gclid, MAX_CLICK_ID) } : {}),
      ...(wbraid ? { wbraid: clamp(wbraid, MAX_CLICK_ID) } : {}),
      ...(capturedAt ? { captured_at: capturedAt } : {}),
    };
  } catch {
    return null;
  }
}
